<template>
  <el-container v-if="showKeFuMessageList" class="kefu">
    <el-header class="kefu-header" height="52px">
      <div class="kefu-title">
        <span class="kefu-title__name">{{ conversation.userNickname }}</span>
        <el-tag
          v-if="conversation.shopName || conversation.shopId != null"
          size="small"
          :type="!conversation.shopId || Number(conversation.shopId) === 0 ? 'success' : 'danger'"
          effect="plain"
        >
          {{ conversation.shopName || (!conversation.shopId || Number(conversation.shopId) === 0 ? '平台' : '店铺') }}
        </el-tag>
      </div>
    </el-header>
    <el-main class="kefu-content">
      <el-scrollbar ref="scrollbarRef" always @scroll="handleScroll">
        <div v-if="refreshContent" ref="innerRef" class="msg-wrap">
          <!-- 消息列表 -->
          <div v-for="(item, index) in getMessageList0" :key="item.id" class="msg-block">
            <div class="msg-meta">
              <!-- 日期 -->
              <div
                v-if="
                  item.contentType !== KeFuMessageContentTypeEnum.SYSTEM && showTime(item, index)
                "
                class="date-message"
              >
                {{ formatDate(item.createTime) }}
              </div>
              <!-- 系统消息 -->
              <div
                v-if="item.contentType === KeFuMessageContentTypeEnum.SYSTEM"
                class="system-message"
              >
                {{ item.content }}
              </div>
            </div>
            <div
              :class="[
                item.senderType === UserTypeEnum.MEMBER
                  ? `ss-row-left`
                  : item.senderType === UserTypeEnum.ADMIN
                    ? `ss-row-right`
                    : ''
              ]"
              class="msg-row"
            >
              <el-avatar
                v-if="item.senderType === UserTypeEnum.MEMBER"
                :size="36"
                :src="resolveMemberAvatar(item)"
                alt="avatar"
              />
              <div
                :class="{
                  'kefu-message': KeFuMessageContentTypeEnum.TEXT === item.contentType
                }"
              >
                <!-- 文本消息 -->
                <MessageItem :message="item">
                  <template v-if="KeFuMessageContentTypeEnum.TEXT === item.contentType">
                    <div
                      v-dompurify-html="replaceEmoji(getMessageContent(item).text || item.content)"
                      class="line-height-normal text-justify h-1/1 w-full"
                    ></div>
                  </template>
                </MessageItem>
                <!-- 图片消息 -->
                <MessageItem :message="item">
                  <el-image
                    v-if="KeFuMessageContentTypeEnum.IMAGE === item.contentType"
                    :initial-index="0"
                    :preview-src-list="[getMessageContent(item).picUrl || item.content]"
                    :src="getMessageContent(item).picUrl || item.content"
                    class="w-200px mx-8px"
                    fit="contain"
                    preview-teleported
                  />
                </MessageItem>
                <!-- 商品消息 -->
                <MessageItem :message="item">
                  <ProductItem
                    v-if="KeFuMessageContentTypeEnum.PRODUCT === item.contentType"
                    :picUrl="getMessageContent(item).picUrl"
                    :price="getMessageContent(item).price"
                    :sales-count="getMessageContent(item).salesCount"
                    :spuId="getMessageContent(item).spuId"
                    :stock="getMessageContent(item).stock"
                    :title="getMessageContent(item).spuName"
                    class="max-w-300px mx-8px"
                  />
                </MessageItem>
                <!-- 订单消息 -->
                <MessageItem :message="item">
                  <OrderItem
                    v-if="KeFuMessageContentTypeEnum.ORDER === item.contentType"
                    :message="item"
                    class="max-w-100% mx-8px"
                  />
                </MessageItem>
              </div>
              <el-avatar
                v-if="item.senderType === UserTypeEnum.ADMIN"
                :size="36"
                :src="resolveStaffAvatar(item)"
                alt="avatar"
              />
            </div>
          </div>
        </div>
      </el-scrollbar>
      <div
        v-show="showNewMessageTip"
        class="newMessageTip flex items-center cursor-pointer"
        @click="handleToNewMessage"
      >
        <span>有新消息</span>
        <Icon class="ml-5px" icon="ep:bottom" />
      </div>
    </el-main>
    <el-footer class="kefu-footer">
      <div class="chat-tools">
        <EmojiSelectPopover @select-emoji="handleEmojiSelect" />
        <PictureSelectUpload class="ml-12px" @send-picture="handleSendPicture" />
      </div>
      <div class="chat-compose">
        <el-input
          v-model="message"
          :rows="3"
          class="kefu-input"
          placeholder="输入消息，Enter 发送，Shift+Enter 换行"
          type="textarea"
          @keydown.enter="onEnterKey"
        />
        <el-button type="primary" class="send-btn" @click="handleSendMessage({})">
          发送
        </el-button>
      </div>
    </el-footer>
  </el-container>
  <el-container v-else class="kefu kefu--empty">
    <el-main class="kefu-empty">
      <div class="kefu-empty__card">
        <Icon icon="ep:chat-dot-round" class="kefu-empty__icon" />
        <p class="kefu-empty__title">选择左侧会话开始接待</p>
        <p class="kefu-empty__desc">会话列表按未读与时间排序，可筛选平台或指定店铺</p>
      </div>
    </el-main>
  </el-container>
</template>

<script lang="ts" setup>
import { ElScrollbar as ElScrollbarType } from 'element-plus'
import { KeFuMessageApi, KeFuMessageRespVO } from '@/api/mall/promotion/kefu/message'
import { KeFuConversationRespVO } from '@/api/mall/promotion/kefu/conversation'
import EmojiSelectPopover from './tools/EmojiSelectPopover.vue'
import PictureSelectUpload from './tools/PictureSelectUpload.vue'
import ProductItem from './message/ProductItem.vue'
import OrderItem from './message/OrderItem.vue'
import { Emoji, useEmoji } from './tools/emoji'
import { KeFuMessageContentTypeEnum } from './tools/constants'
import { isEmpty } from '@/utils/is'
import { UserTypeEnum } from '@/utils/constants'
import { formatDate } from '@/utils/formatTime'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { debounce } from 'lodash-es'
import { jsonParse } from '@/utils'
import { useMallKefuStore } from '@/store/modules/mall/kefu'
import { useUserStore } from '@/store/modules/user'

dayjs.extend(relativeTime)

defineOptions({ name: 'KeFuMessageList' })

const message = ref('') // 消息弹窗
const { replaceEmoji } = useEmoji()
const messageTool = useMessage()
const messageList = ref<KeFuMessageRespVO[]>([]) // 消息列表
const conversation = ref<KeFuConversationRespVO>({} as KeFuConversationRespVO) // 用户会话
const showNewMessageTip = ref(false) // 显示有新消息提示
const queryParams = reactive({
  conversationId: 0,
  createTime: undefined
})
const total = ref(0) // 消息总条数
const refreshContent = ref(false) // 内容刷新,主要解决会话消息页面高度不一致导致的滚动功能精度失效
const kefuStore = useMallKefuStore() // 客服缓存
const userStore = useUserStore()

/** 用户无头像 → 不传 src，el-avatar 显示灰色色块 */
const resolveMemberAvatar = (item: KeFuMessageRespVO) => {
  const raw = item.senderAvatar || conversation.value.userAvatar
  return raw && String(raw).trim() ? String(raw).trim() : undefined
}
/** 客服：有头像用头像，否则当前登录管理员头像；都没有则灰色色块 */
const resolveStaffAvatar = (item: KeFuMessageRespVO) => {
  const raw = item.senderAvatar || userStore.getUser?.avatar
  return raw && String(raw).trim() ? String(raw).trim() : undefined
}

/** 获悉消息内容 */
const getMessageContent = computed(() => (item: any) => jsonParse(item.content))
/** 获得消息列表 */
const getMessageList = async () => {
  const res = await KeFuMessageApi.getKeFuMessageList(queryParams)
  if (isEmpty(res)) {
    // 当返回的是空列表说明没有消息或者已经查询完了历史消息
    skipGetMessageList.value = true
    return
  }
  queryParams.createTime = formatDate(res.at(-1).createTime) as any

  // 情况一：加载最新消息
  if (!queryParams.createTime) {
    messageList.value = res
  } else {
    // 情况二：加载历史消息
    for (const item of res) {
      pushMessage(item)
    }
  }
  refreshContent.value = true
}

/** 添加消息 */
const pushMessage = (message: any) => {
  if (messageList.value.some((val) => val.id === message.id)) {
    return
  }
  messageList.value.push(message)
}

/** 按照时间倒序，获取消息列表 */
const getMessageList0 = computed(() => {
  messageList.value.sort((a: any, b: any) => a.createTime - b.createTime)
  return messageList.value
})

/** 刷新消息列表 */
const refreshMessageList = async (message?: any) => {
  if (!conversation.value) {
    return
  }

  if (typeof message !== 'undefined') {
    // 当前查询会话与消息所属会话不一致则不做处理
    if (message.conversationId !== conversation.value.id) {
      return
    }
    pushMessage(message)
  } else {
    queryParams.createTime = undefined
    await getMessageList()
  }

  if (loadHistory.value) {
    // 右下角显示有新消息提示
    showNewMessageTip.value = true
  } else {
    // 滚动到最新消息处
    await handleToNewMessage()
  }
}

/** 获得新会话的消息列表, 点击切换时，读取缓存；然后异步获取新消息，merge 下； */
const getNewMessageList = async (val: KeFuConversationRespVO) => {
  // 1. 缓存当前会话消息列表
  kefuStore.saveMessageList(conversation.value.id, messageList.value)
  // 2.1 会话切换,重置相关参数
  messageList.value = kefuStore.getConversationMessageList(val.id) || []
  total.value = messageList.value.length || 0
  loadHistory.value = false
  refreshContent.value = false
  skipGetMessageList.value = false
  // 2.2 设置会话相关属性
  conversation.value = val
  queryParams.conversationId = val.id
  queryParams.createTime = undefined
  // 3. 获取消息
  await refreshMessageList()
}
defineExpose({ getNewMessageList, refreshMessageList })

const showKeFuMessageList = computed(() => !isEmpty(conversation.value)) // 是否显示聊天区域
const skipGetMessageList = ref(false) // 跳过消息获取

/** 处理表情选择 */
const handleEmojiSelect = (item: Emoji) => {
  message.value += item.name
}

/** 处理图片发送 */
const handleSendPicture = async (picUrl: string) => {
  // 组织发送消息
  const msg = {
    conversationId: conversation.value.id,
    contentType: KeFuMessageContentTypeEnum.IMAGE,
    content: JSON.stringify({ picUrl })
  }
  await sendMessage(msg)
}

/** Enter 发送；Shift+Enter 换行 */
const onEnterKey = (event: KeyboardEvent) => {
  if (event.shiftKey) return
  event.preventDefault()
  void handleSendMessage(event)
}

/** 发送文本消息 */
const handleSendMessage = async (event: any = {}) => {
  // shift 不发送
  if (event?.shiftKey) {
    return
  }
  // 1. 校验消息是否为空
  if (isEmpty(unref(message.value)?.trim())) {
    messageTool.notifyWarning('请输入消息后再发送哦！')
    message.value = ''
    return
  }
  // 2. 组织发送消息
  const msg = {
    conversationId: conversation.value.id,
    contentType: KeFuMessageContentTypeEnum.TEXT,
    content: JSON.stringify({ text: message.value })
  }
  await sendMessage(msg)
}

/** 真正发送消息 【共用】*/
const sendMessage = async (msg: any) => {
  // 发送消息
  await KeFuMessageApi.sendKeFuMessage(msg)
  message.value = ''
  // 加载消息列表
  await refreshMessageList()
  // 更新会话缓存
  await kefuStore.updateConversation(conversation.value.id)
}

/** 滚动到底部 */
const innerRef = ref<HTMLDivElement>()
const scrollbarRef = ref<InstanceType<typeof ElScrollbarType>>()
const scrollToBottom = async () => {
  // 1. 首次加载时滚动到最新消息，如果加载的是历史消息则不滚动
  if (loadHistory.value) {
    return
  }
  // 2.1 滚动到最新消息，关闭新消息提示
  await nextTick()
  scrollbarRef.value!.setScrollTop(innerRef.value!.clientHeight)
  showNewMessageTip.value = false
  // 2.2 消息已读
  await KeFuMessageApi.updateKeFuMessageReadStatus(conversation.value.id)
}

/** 查看新消息 */
const handleToNewMessage = async () => {
  loadHistory.value = false
  await scrollToBottom()
}

const loadHistory = ref(false) // 加载历史消息
/** 处理消息列表滚动事件(debounce 限流) */
const handleScroll = debounce(({ scrollTop }) => {
  if (skipGetMessageList.value) {
    return
  }
  // 触顶自动加载下一页数据
  if (Math.floor(scrollTop) === 0) {
    handleOldMessage()
  }
  const wrap = scrollbarRef.value?.wrapRef
  // 触底重置
  if (Math.abs(wrap!.scrollHeight - wrap!.clientHeight - wrap!.scrollTop) < 1) {
    loadHistory.value = false
    refreshMessageList()
  }
}, 200)
/** 加载历史消息 */
const handleOldMessage = async () => {
  // 记录已有页面高度
  const oldPageHeight = innerRef.value?.clientHeight
  if (!oldPageHeight) {
    return
  }
  loadHistory.value = true
  await getMessageList()
  // 等页面加载完后，获得上一页最后一条消息的位置，控制滚动到它所在位置
  scrollbarRef.value!.setScrollTop(innerRef.value!.clientHeight - oldPageHeight)
}

/**
 * 是否显示时间
 *
 * @param {*} item - 数据
 * @param {*} index - 索引
 */
const showTime = computed(() => (item: KeFuMessageRespVO, index: number) => {
  if (unref(messageList.value)[index + 1]) {
    let dateString = dayjs(unref(messageList.value)[index + 1].createTime).fromNow()
    return dateString !== dayjs(unref(item).createTime).fromNow()
  }
  return false
})
</script>

<style lang="scss" scoped>
.kefu {
  position: relative;
  /* 左栏 300 + 右栏 320；高度锁死父级，避免主区撑开把输入区挤出视口 */
  width: calc(100% - 300px - 320px);
  height: 100%;
  overflow: hidden;
  background: var(--kefu-chat-bg, #f4f6f9);

  &--empty {
    background: var(--kefu-chat-bg, #f4f6f9);
  }

  .kefu-header {
    display: flex;
    flex-shrink: 0;
    padding: 0 16px;
    background: var(--kefu-panel, #fff);
    border-bottom: 1px solid var(--kefu-border, #e8ecf1);
    align-items: center;
  }

  .kefu-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    &__name {
      overflow: hidden;
      font-size: 15px;
      font-weight: 600;
      color: var(--kefu-text, #1f2937);
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .kefu-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;

    &__card {
      max-width: 320px;
      padding: 28px 24px;
      text-align: center;
    }

    &__icon {
      margin-bottom: 12px;
      font-size: 40px;
      color: var(--kefu-accent, #1e3a5f);
      opacity: 0.55;
    }

    &__title {
      margin: 0 0 8px;
      font-size: 15px;
      font-weight: 600;
      color: var(--kefu-text, #1f2937);
    }

    &__desc {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      color: var(--kefu-muted, #8a94a6);
    }
  }

  &-content {
    position: relative;
    flex: 1;
    width: 100%;
    min-height: 0;
    height: auto !important;
    padding: 12px 8px;
    margin: 0;
    overflow: hidden;

    :deep(.el-scrollbar) {
      height: 100%;
    }

    .msg-wrap {
      width: 100%;
      padding: 4px 8px 12px;
    }

    .msg-block {
      width: 100%;
    }

    .msg-meta {
      display: flex;
      justify-content: center;
      margin-bottom: 12px;
    }

    .msg-row {
      display: flex;
      width: 100%;
      margin-bottom: 16px;
      align-items: flex-start;
    }

    .newMessageTip {
      position: absolute;
      right: 24px;
      bottom: 24px;
      z-index: 2;
      padding: 8px 14px;
      font-size: 12px;
      color: var(--kefu-accent, #1e3a5f);
      background: var(--kefu-panel, #fff);
      border: 1px solid var(--kefu-border, #e8ecf1);
      border-radius: 20px;
      box-shadow: 0 4px 12px rgb(15 23 42 / 8%);
    }

    .ss-row-left {
      justify-content: flex-start;

      .kefu-message {
        margin-top: 2px;
        margin-left: 10px;
        background: #fff;
        border: 1px solid var(--kefu-border, #e8ecf1);
        border-radius: 4px 12px 12px;
      }
    }

    .ss-row-right {
      justify-content: flex-end;

      .kefu-message {
        margin-top: 2px;
        margin-right: 10px;
        color: #fff;
        background: var(--kefu-accent, #1e3a5f);
        border-radius: 12px 4px 12px 12px;
      }
    }

    .kefu-message {
      width: auto;
      max-width: 58%;
      padding: 8px 12px;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
      word-break: break-word;
    }

    .date-message,
    .system-message {
      width: fit-content;
      padding: 2px 10px;
      font-size: 12px;
      line-height: 1.6;
      color: var(--kefu-muted, #8a94a6);
      background: rgb(148 163 184 / 18%);
      border-radius: 10px;
    }
  }

  .kefu-footer {
    display: flex;
    height: auto !important;
    min-height: 132px;
    max-height: 200px;
    padding: 0;
    margin: 0;
    overflow: hidden;
    background: var(--kefu-panel, #fff);
    border-top: 1px solid var(--kefu-border, #e8ecf1);
    flex-direction: column;
    flex-shrink: 0;

    .chat-tools {
      display: flex;
      width: 100%;
      height: 40px;
      padding: 0 12px;
      overflow: hidden;
      flex-shrink: 0;
      align-items: center;
    }

    .chat-compose {
      display: flex;
      flex: 1;
      gap: 10px;
      min-height: 0;
      padding: 0 12px 12px;
      align-items: flex-end;
    }

    .kefu-input {
      flex: 1;
      min-height: 72px;
    }

    .send-btn {
      flex-shrink: 0;
      height: 36px;
      padding: 0 18px;
      background: var(--kefu-accent, #1e3a5f);
      border-color: var(--kefu-accent, #1e3a5f);

      &:hover,
      &:focus {
        background: #274b7a;
        border-color: #274b7a;
      }
    }
  }

  :deep(textarea) {
    background: var(--kefu-chat-bg, #f4f6f9);
    border-radius: 8px;
    resize: none;
  }

  :deep(.el-textarea__inner) {
    box-shadow: none !important;
  }

  :deep(.el-input__wrapper) {
    border-radius: 8px;
    box-shadow: none !important;
  }
}
</style>
